import { FocusMonitor } from '@angular/cdk/a11y';
import {
  booleanAttribute,
  Component,
  ContentChild,
  DoCheck,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroupDirective, NgControl, NgForm, Validators } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';

import { FilteredSelectOptionDirective } from './filtered-select-option.directive';
import { ErrorStateMatcher } from '@angular/material/core';

@Component({
  selector: 'sc-filtered-select',
  templateUrl: './filtered-select.component.html',
  styleUrl: './filtered-select.component.scss',

  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: forwardRef(() => FilteredSelectComponent)
    }
  ],

  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class FilteredSelectComponent
  implements MatFormFieldControl<any>, ControlValueAccessor, DoCheck, OnInit, OnChanges, OnDestroy
{
  // Static
  static nextId = 0;

  // Inputs
  @Input() allowNullify = false;
  @Input() compareWith: (o1: any, o2: any) => boolean = (o1, o2) => o1 === o2;
  @Input() displayFn: (value: any) => string = (value) => {
    if (typeof value === 'object') {
      return value[this.displayField] ?? '';
    }

    return value;
  };
  @Input() displayField: string;
  @Input() options: any[] = [];
  @Input() placeholder = '';
  @Input({ transform: booleanAttribute }) required: boolean = false;
  @Input() valueField: string;

  @Output() selectionChanged: Subject<any> = new Subject<any>();

  // Template
  @ContentChild(FilteredSelectOptionDirective, { read: TemplateRef })
  optionTemplate: TemplateRef<any>;

  @ViewChild('filterInput') filterInput: ElementRef<HTMLInputElement>;

  // Form controls
  filterControl = new FormControl('');
  innerControl = new FormControl();

  // State
  controlType = 'filterable-select';
  describedBy = '';
  filteredOptions: any[] = [];
  focused = false;
  id = `filterable-select-${FilteredSelectComponent.nextId++}`;
  stateChanges = new Subject<void>();
  touched = false;
  private _value: any;

  // MatFormFieldControl required properties
  disabled = false;
  errorState = false;
  autofilled?: boolean;
  userAriaDescribedBy?: string;
  disableAutomaticLabeling?: boolean;

  // ControlValueAccessor callbacks
  onChange = (value: any) => {
    this.selectionChanged.next(value);
  };
  onTouched = () => {};

  constructor(
    private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>,
    private errorStateMatacher: ErrorStateMatcher,
    @Optional() @Self() public ngControl: NgControl,
    @Optional() private parentForm: NgForm,
    @Optional() private parentFormGroup: FormGroupDirective
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.fm.monitor(this.elRef.nativeElement, true).subscribe((origin) => {
      this.focused = !!origin;
      this.stateChanges.next();
      if (!origin) {
        this.onTouched();
      }
    });
  }

  ngOnInit(): void {
    this.filteredOptions = this.options;

    if (this.ngControl?.control) {
      const control = this.ngControl.control;

      if (control.hasValidator && control.hasValidator(Validators.required)) {
        this.required = true;
      }

      control.statusChanges.subscribe(() => {
        this.required = !!control.hasValidator(Validators.required);
      });
    }

    this.filterControl.valueChanges.subscribe((val) => {
      const filterValue = val?.toLowerCase() || '';
      this.filteredOptions = this.options.filter((option) => {
        const value = this.displayFn(option) || '';

        return value.toLowerCase().includes(filterValue) || option[this.valueField] === this._value;
      });
    });

    this.innerControl.valueChanges.subscribe((value) => {
      this._value = value;
      this.onChange(value);
      this.stateChanges.next();
    });
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.filteredOptions = this.options;
      this.filterControl.setValue('', { emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  get value(): any {
    return this._value;
  }

  set value(val: any) {
    this._value = val;
    this.innerControl.setValue(val, { emitEvent: false });

    this.onChange(val);
    this.stateChanges.next();
  }

  get empty(): boolean {
    return !this.value;
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(_event: MouseEvent): void {
    (this.elRef.nativeElement.querySelector('mat-select') as HTMLElement)?.focus();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      event.stopPropagation();
    }
  }

  onOpenedChange(state: boolean): void {
    if (state) {
      this.filterInput?.nativeElement.focus();
    } else {
      this.filterControl.setValue('');
    }
  }

  onSelectionChange(value: any): void {
    this.selectionChanged.next(value);
  }

  writeValue(value: any): void {
    this._value = value;
    this.innerControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.innerControl.disable() : this.innerControl.enable();
    this.disabled = isDisabled;
    this.stateChanges.next();
  }

  private updateErrorState(): void {
    const control = this.ngControl.control;
    const parent = this.parentFormGroup || this.parentForm;

    const newState = this.errorStateMatacher.isErrorState(control, parent);

    if (newState !== this.errorState) {
      this.errorState = newState;
      this.stateChanges.next();
    }
  }
}
